<script lang="ts">
	import {
		ButtonV2,
		Container,
		Input,
		Modal,
		Text,
		Textarea,
		toast,
		Token,
	} from '@Shelly/shelly-design-system/index';
	import { format } from '../../../services/i18n';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import { postReq } from 'src/services/http';
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
	import { save_home } from 'src/helpers/home_helpers';
	import { isMultiHome, addInstallerCode } from 'src/helpers/installer_helpers';
	import { capFirstChar, conditionsFunc } from 'src/helpers/utilities';

	export let message = '';
	export let showModal = false;
	export let addReqLoading = false;
	export let installerCode = '';
	export let regrantAccessCode = '';
	export let installerData: { [key: string]: string } = {};

	const min = 10;
	const max = 8760;
	let accessHours: string | number = '';
	let error = '';

	const onCloseGrantAccessModal = (regrant = false) => {
		error = '';
		message = '';
		accessHours = '';
		predefinedAccessPeriods = predefinedAccessPeriods.map((currentPeriod) => ({
			...currentPeriod,
			selected: currentPeriod.value === 12,
		}));
		if (regrant) {
			regrantAccessCode = '';
		}
		showModal = false;
	};

	const onGrantAccess = async () => {
		const custom_time =
			Number(accessHours) ||
			predefinedAccessPeriods.find((period) => period.selected)?.value ||
			12;

		const now = new Date();
		now.setHours(now.getHours() + custom_time);

		addReqLoading = true;
		const res: CloudApiResponse = await postReq(
			`user/${!regrantAccessCode ? 'add_installer' : 'installer_access'}`,
			{
				icode: regrantAccessCode || installerCode,
				env: import.meta.env.VITE_APP_BUILD,
				custom_time,
				...(message.trim() && { message }),
			},
		);

		if (res.isok) {
			if (!regrantAccessCode) {
				const multiHome = isMultiHome($user_data);
				const userId = $user_data?.user_id;
				const updatedIcodes = addInstallerCode(
					$user_settings.icodes,
					installerData.code,
					multiHome,
					userId,
				);
				user_settings.set({ ...$user_settings, icodes: updatedIcodes });
			}
			save_home('installers_time', {
				...$home_data?.installers_time,
				[installerData.code]: now.getTime(),
			});
			save_home('icodes', {
				...($home_data.icodes && { ...$home_data.icodes }),
				[installerData.code]: installerData,
			});
			toast.open({
				type: 'success',
				title: $format('alert_success_granting_access_installer', {
					default: 'Successfully granting access to the installer',
				}),
			});
		} else {
			if (
				['Error granting access.', 'Could not add this installer.'].includes(
					res?.errors?.message,
				)
			) {
				toast.open({
					type: 'danger',
					title: $format('toast_error_granting_access', {
						default: 'Error granting access',
					}),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('toast_wrong_installer_code', {
						default: 'Wrong installer code',
					}),
				});
			}
		}

		installerCode = '';
		regrantAccessCode = '';
		addReqLoading = false;
		onCloseGrantAccessModal(!!regrantAccessCode);
	};

	$: title = regrantAccessCode
		? $format('button_re_grant_access', { default: 'Grant access again' })
		: $format('button_grant_access', { default: 'Grant access' });

	$: installerTextCred = `<span class="h2-styles">${installerData?.electrician_name || installerData.name}</span> (<a href="mailto:${installerData?.electrician_email || installerData.email}" class="link-styles">${installerData?.electrician_email || installerData.email}</a>)`;
	$: installerTextCode = `<span class="h2-styles">${installerData?.electrician_code || installerData.code}</span>`;
	$: installerCompany = `<span class="h2-styles">${installerData.company_name}</span>`;
	$: installerTextModal = installerData?.electrician_code
		? $format('text_modal_grant_access_4', {
				default:
					'Are you sure you want to grant access to {installerTextCred} from {installerCompany} using code {installerTextCode}?',
				values: { installerTextCred, installerCompany, installerTextCode },
			})
		: $format('text_modal_grant_access_3', {
				default:
					'Are you sure you want to grant access to {installerTextCred} using code {installerTextCode}?',
				values: { installerTextCred, installerTextCode },
			});

	$: predefinedAccessPeriods = [
		{
			title: `12 ${capFirstChar($format('select_units_hours', { default: 'Hours' }))}`,
			selected: true,
			value: 12,
		},
		{
			title: `1 ${capFirstChar($format('option_day', { default: 'Day' }))}`,
			selected: false,
			value: 24,
		},
		{
			title: `3 ${capFirstChar($format('label_days', { default: 'Days' }))}`,
			selected: false,
			value: 72,
		},
		{
			title: `5 ${capFirstChar($format('label_days', { default: 'Days' }))}`,
			selected: false,
			value: 120,
		},
		{
			title: `1 ${capFirstChar($format('option_week', { default: 'Week' }))}`,
			selected: false,
			value: 168,
		},
		{
			title: `2 ${capFirstChar($format('label_weeks', { default: 'Weeks' }))}`,
			selected: false,
			value: 336,
		},
		{
			title: `1 ${capFirstChar($format('option_month', { default: 'Month' }))}`,
			selected: false,
			value: 720,
		},
		{
			title: `2 ${capFirstChar($format('option_months', { default: 'Months' }))}`,
			selected: false,
			value: 1460,
		},
		{
			title: `3 ${capFirstChar($format('option_months', { default: 'Months' }))}`,
			selected: false,
			value: 2190,
		},
		{
			title: `4 ${capFirstChar($format('option_months', { default: 'Months' }))}`,
			selected: false,
			value: 2920,
		},
		{
			title: `5 ${capFirstChar($format('option_months', { default: 'Months' }))}`,
			selected: false,
			value: 3650,
		},
		{
			title: `6 ${capFirstChar($format('option_months', { default: 'Months' }))}`,
			selected: false,
			value: 4380,
		},
		{
			title: `1 ${capFirstChar($format('option_year', { default: 'Year' }))}`,
			selected: false,
			value: 8760,
		},
	];
</script>

<Modal
	{title}
	{showModal}
	on:close={() => onCloseGrantAccessModal(!!regrantAccessCode)}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text class="mb-m"
			>{$format('text_modal_grant_access_1', {
				default:
					'Granting access to an installer will allow them to access your account until you revoke it.',
			})}</Text
		>

		<Text class="mb-m"
			>{$format('text_modal_grant_access_2', {
				default:
					'For a custom duration, you can select one of the predefined access periods or manually adjust the access time.',
			})}</Text
		>

		<Text class="mb-xl">{@html installerTextModal}</Text>

		<div class="mb-l d-flex flex-wrap align-items-start justify-content-center">
			{#each predefinedAccessPeriods as period, index}
				<Token
					id="token-o66vgxk"
					title={period.title}
					selected={period.selected}
					class={index === predefinedAccessPeriods.length - 1 ? 'mb-s' : 'mr-s mb-s'}
					size="m"
					on:click={() => {
						error = '';
						accessHours = '';
						predefinedAccessPeriods.forEach(
							(currentPeriod) => (currentPeriod.selected = false),
						);
						period.selected = true;
					}}
				/>
			{/each}
		</div>

		<div class="d-flex flex-direction-column align-items-center">
			<Text element="h2" class="mb-s">
				{$format('label_choose_time', { default: 'Choose time' })}
			</Text>

			<Input
				aria-label={$format('input_access_time', { default: 'Access time in hours' })}
				width="l"
				type="number"
				icon="fas fa-timer"
				{min}
				{max}
				info={$format('installer_custom_info_validation_new', {
					default: 'The value must be between {min} and {max} hours',
					values: { min, max },
				})}
				id="input-rvkn8wb"
				bind:value={accessHours}
				bind:error
				on:input={(e) => {
					predefinedAccessPeriods = predefinedAccessPeriods.map((currentPeriod) => ({
						...currentPeriod,
						selected: false,
					}));
					if (conditionsFunc(e.target.value) || e.target.value < min || e.target.value > max) {
						error = $format('installer_custom_error_validation_new', {
							default:
								'The value must be a whole number and between {min} and {max} hours',
							values: { min, max },
						});
					} else {
						error = '';
					}
				}}
			/>
		</div>

		{#if regrantAccessCode}
			<!-- DO NOT REMOVE COMMENT: Padding references -->
			<Textarea
				label={$format('textarea_installer_message', {
					default: 'Send a message to the installer if you want...',
				})}
				bind:value={message}
				class="mt-l"
				size="m"
				id="textarea-cge17lb"
				aria-labelledby="textarea-cge17lb"
			/>
		{/if}

		{#if installerData?.electrician_code}
			<Text styleType="miniature" class="mt-l text-center"
				>{$format('text_modal_grant_access_5', {
					default:
						'Keep in mind that {installerCompany} may change your designated installer at any time. You can still revoke access at any time as well.',
					values: { installerCompany: installerData.company_name },
				})}</Text
			>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={() => onCloseGrantAccessModal(!!regrantAccessCode)}
			>{$format('button_cancel', { default: 'Cancel' })}</ButtonV2
		>
		<ButtonV2
			variant="primary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			disabled={!!error || addReqLoading}
			on:click={onGrantAccess}
		>
			{title}
		</ButtonV2>
	</div>
</Modal>
